import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLanguage } from "./LanguajeContext";
import { FaExclamationTriangle, FaCheck, FaTimes } from "react-icons/fa";
import translations from "../utils/serviceform.translate.js";

// Componente de mensaje de error mejorado
const ErrorMessage = ({ message, onClose }) => (
  <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 text-white rounded-xl flex items-start gap-3 animate-fade-in">
    <div className="flex-shrink-0 mt-0.5">
      <FaExclamationTriangle className="text-red-400 text-xl" />
    </div>
    <div className="flex-1">
      <p className="font-medium">{message}</p>
    </div>
    {onClose && (
      <button
        onClick={onClose}
        className="flex-shrink-0 text-red-300 hover:text-white transition-colors"
      >
        <FaTimes />
      </button>
    )}
  </div>
);

// Componente de mensaje de éxito mejorado
const SuccessMessage = ({ message, onClose }) => (
  <div className="mb-4 p-4 bg-green-500/20 border border-green-500/50 text-white rounded-xl flex items-start gap-3 animate-fade-in">
    <div className="flex-shrink-0 mt-0.5">
      <FaCheck className="text-green-400 text-xl" />
    </div>
    <div className="flex-1">
      <p className="font-medium">{message}</p>
    </div>
    {onClose && (
      <button
        onClick={onClose}
        className="flex-shrink-0 text-green-300 hover:text-white transition-colors"
      >
        <FaTimes />
      </button>
    )}
  </div>
);

// Función para formatear números con separadores de miles
const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Función para analizar números formateados
const parseFormattedNumber = (str) => {
  return parseFloat(str.replace(/,/g, ""));
};

export default function ServiceForm({
  onSubmit,
  initialData = {},
  isEditing = false,
  onSuccess,
  externalError,
  onExternalErrorClear,
}) {
  const { language } = useLanguage();
  const t = translations[language] ?? translations.es;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    max_capacity: "",
    category: "",
    price: "",
    activate: true,
    image: null,
  });

  const [preview, setPreview] = useState(null);
  const [displayPrice, setDisplayPrice] = useState("");
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedFlash, setSavedFlash] = useState("");

  useEffect(() => {
    const newFormData = {
      name: initialData.name ?? "",
      description: initialData.description ?? "",
      max_capacity:
        initialData.max_capacity === 0 || initialData.max_capacity
          ? String(initialData.max_capacity)
          : "",
      category: initialData.category ?? "",
      price:
        initialData.price === 0 || initialData.price
          ? String(initialData.price)
          : "",
      image: null,
    };

    setFormData(newFormData);

    if (newFormData.price && !isNaN(newFormData.price)) {
      setDisplayPrice(formatNumber(newFormData.price));
    } else {
      setDisplayPrice("");
    }

    if (initialData.image) {
      setPreview(initialData.image);
    }
  }, [initialData]);

  useEffect(() => {
    if (externalError) {
      setError(null);
    }
  }, [externalError]);

  const renderError = useMemo(() => {
    if (externalError) return externalError;
    if (!error) return "";
    if (error.kind === "key") return t[error.value] ?? t.unknownError;
    return error.value || t.unknownError;
  }, [error, externalError, t]);

  const handlePriceChange = useCallback(
    (e) => {
      const value = e.target.value.replace(/,/g, "");

      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        setError(null);
        setSavedFlash("");
        if (externalError && onExternalErrorClear) {
          onExternalErrorClear();
        }

        setFormData((prev) => ({ ...prev, price: value }));

        if (value === "") {
          setDisplayPrice("");
        } else {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            setDisplayPrice(formatNumber(value));
          }
        }
      }
    },
    [externalError, onExternalErrorClear]
  );

  const handleChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;

      if (name === "price") {
        return;
      }

      const val = type === "checkbox" ? checked : value;
      setError(null);
      setSavedFlash("");
      if (externalError && onExternalErrorClear) {
        onExternalErrorClear();
      }
      setFormData((prev) => ({ ...prev, [name]: val }));
    },
    [externalError, onExternalErrorClear]
  );





  const handleFileChange = (e) => {
    
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, image: file || null }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const validate = () => {
    if (!formData.name.trim()) return { kind: "key", value: "requiredName" };
    if (!formData.description.trim())
      return { kind: "key", value: "requiredDescription" };
    if (!formData.category.trim())
      return { kind: "key", value: "requiredCategory" };

    const cap = Number(formData.max_capacity);
    if (
      formData.max_capacity === "" ||
      !Number.isFinite(cap) ||
      cap < 1
    )
      return { kind: "key", value: "invalidCapacity" };

    if (formData.price === "") return { kind: "key", value: "priceRequired" };

    const price = Number(formData.price);
    if (!Number.isFinite(price) || price < 0)
      return { kind: "key", value: "invalidPrice" };

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSavedFlash("");
    const vErr = validate();
    if (vErr) {
      setError(vErr);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    if (externalError && onExternalErrorClear) {
      onExternalErrorClear();
    }

    const payload = {
      ...formData,
      price: Number(formData.price),
      max_capacity: Number(formData.max_capacity),
    };

    try {
      await onSubmit?.(payload);
      setSavedFlash(t.saved);
      onSuccess?.();
    } catch (err) {
      setError({ kind: "msg", value: err?.message || "" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeError = () => {
    setError(null);
    if (externalError && onExternalErrorClear) {
      onExternalErrorClear();
    }
  };

  const closeSuccess = () => {
    setSavedFlash("");
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-[#335066] p-6 rounded-xl text-white shadow-xl space-y-5 max-w-lg mx-auto"
      noValidate
    >
      <h2 className="text-2xl font-bold mb-3 text-center">
        {isEditing ? t.titleEdit : t.titleCreate}
      </h2>

      {renderError && (
        <ErrorMessage message={renderError} onClose={closeError} />
      )}

      {savedFlash && !renderError && (
        <SuccessMessage message={savedFlash} onClose={closeSuccess} />
      )}

      <div>
        <label htmlFor="name" className="block mb-1 font-medium">
          {t.name}
        </label>
        <input
          id="name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="w-full p-2 rounded bg-[#2D4A5E] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-green-400"
          autoComplete="off"
        />
      </div>

      <div>
        <label htmlFor="description" className="block mb-1 font-medium">
          {t.description}
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="w-full p-2 rounded bg-[#2D4A5E] border border-gray-600 resize-none focus:outline-none focus:ring-2 focus:ring-green-400"
        />
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <label htmlFor="max_capacity" className="block mb-1 font-medium">
            {t.maxCapacity}
          </label>
          <input
            id="max_capacity"
            type="number"
            name="max_capacity"
            value={formData.max_capacity}
            onChange={handleChange}
            className="w-full p-2 rounded bg-[#2D4A5E] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-green-400"
            min={1}
            inputMode="numeric"
          />
        </div>

        <div className="flex-1">
          <label htmlFor="category" className="block mb-1 font-medium">
            {t.category}
          </label>
          <input
            id="category"
            type="text"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full p-2 rounded bg-[#2D4A5E] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-green-400"
          />
        </div>
      </div>

      <div>
        <label htmlFor="price" className="block mb-1 font-medium">
          {t.price}
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-300">
            ₡
          </span>
          <input
            id="price"
            type="text"
            name="price"
            value={displayPrice}
            onChange={handlePriceChange}
            className="w-full pl-8 p-2 rounded bg-[#2D4A5E] border border-gray-600 focus:outline-none focus:ring-2 focus:ring-green-400"
            inputMode="decimal"
            placeholder="0"
          />
        </div>
        <p className="text-xs text-gray-300 mt-1">Escriba el precio en colones</p>
      </div>

      <div>
        <label htmlFor="image" className="block mb-1 font-medium">
          {t.image}
        </label>
        <input
          id="image"
          type="file"
          name="image"
          accept="image/*"
          onChange={handleFileChange}
          className="w-full text-sm text-gray-300"
        />
        {preview && (
          <img
            src={preview}
            alt="Vista previa"
            className="mt-2 rounded-lg max-h-40 object-cover border border-gray-600"
          />
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full bg-green-400 text-black font-semibold py-2 rounded hover:bg-green-300 transition ${
          isSubmitting ? "opacity-70 cursor-not-allowed" : ""
        }`}
      >
        {isSubmitting
          ? isEditing
            ? t.updating
            : t.creating
          : isEditing
          ? t.submitEdit
          : t.submitCreate}
      </button>
    </form>
  );
}
